import styled, { createGlobalStyle } from 'styled-components';
import PoppinsRegular from 'src/assets/fonts/Poppins-Regular.ttf';
import PoppinsMedium from 'src/assets/fonts/Poppins-Medium.ttf';
import PoppinsBold from 'src/assets/fonts/Poppins-Bold.ttf';
import PoppinsItalic from 'src/assets/fonts/Poppins-Italic.ttf';

// The exact Poppins TTFs the export-runner embeds, mapped the same way as its
// Font.register (Bold → weight 600). Applied to the editor content so text lays
// out with identical glyph metrics → identical line wraps as the rendered PDF.
export const FreeformFonts = createGlobalStyle`
  @font-face {
    font-family: 'PoppinsExport';
    font-style: normal;
    font-weight: 400;
    font-display: swap;
    src: url(${PoppinsRegular}) format('truetype');
  }
  @font-face {
    font-family: 'PoppinsExport';
    font-style: normal;
    font-weight: 500;
    font-display: swap;
    src: url(${PoppinsMedium}) format('truetype');
  }
  @font-face {
    font-family: 'PoppinsExport';
    font-style: normal;
    font-weight: 600;
    font-display: swap;
    src: url(${PoppinsBold}) format('truetype');
  }
  @font-face {
    font-family: 'PoppinsExport';
    font-style: italic;
    font-weight: 400;
    font-display: swap;
    src: url(${PoppinsItalic}) format('truetype');
  }
`;

// Dev-tool chrome — literal hex (matching the sibling ExportRunnerDebugButton
// component) rather than theme tokens, so this page stays self-contained.

export const Container = styled.div`
  padding: 24px 32px 48px;
  font-family: 'Poppins', 'Inter', system-ui, sans-serif;
  color: #111827;
`;

export const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 18px;
`;

export const Subtitle = styled.div`
  font-size: 13px;
  color: #6b7280;
`;

export const Layout = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 20px;

  ${({ theme }) => theme.breakpoints.down('md')} {
    flex-direction: column;
  }
`;

export const EditorPane = styled.div`
  flex: 1;
  min-width: 0;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

export const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  padding: 8px 10px;
  border-bottom: 1px solid #e9ecff; /* stroke */
  background: #f6f6f9; /* cultured */
  position: sticky;
  top: 0;
  z-index: 1;
`;

export const ToolbarButton = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 30px;
  padding: 0 8px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  color: ${({ $active }) => ($active ? '#ffffff' : '#022033')}; /* prussianBlue */
  background: ${({ $active }) => ($active ? '#022033' : '#ffffff')};
  border: 1px solid ${({ $active }) => ($active ? '#022033' : '#e9ecff')};
  border-radius: 6px;
  cursor: pointer;
  transition:
    background 120ms ease,
    border-color 120ms ease,
    color 120ms ease;
  &:hover:not(:disabled) {
    border-color: #6a7590; /* cflowerBlue */
    background: ${({ $active }) => ($active ? '#022033' : '#f6f6f9')};
  }
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

export const ToolbarDivider = styled.span`
  width: 1px;
  height: 20px;
  background: #e9ecff;
  margin: 0 4px;
`;

// The custom-component insert action — deliberately NOT a primitive. Same clean
// chrome as the formatting buttons (so it never reads as an error state), set
// apart by a gradient chip + a text label rather than a single glyph.
export const InsertComponentButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 30px;
  padding: 0 12px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.2px;
  color: #022033;
  background: #ffffff;
  border: 1px solid #e9ecff;
  border-radius: 6px;
  cursor: pointer;
  transition:
    background 120ms ease,
    border-color 120ms ease;

  &::before {
    content: '';
    width: 12px;
    height: 12px;
    flex-shrink: 0;
    border-radius: 3px;
    background: linear-gradient(106.11deg, #e62e78 0.01%, #fd5630 98.53%);
  }

  &:hover {
    border-color: #6a7590;
    background: #f6f6f9;
  }
`;

// The scrollable "desk" the page sheet sits on. A block-level page child with
// `margin: 0 auto` centres when it fits and overflows to the right (scrollable)
// when it doesn't — no left-edge clipping for a wide landscape page.
export const EditorScroll = styled.div`
  padding: 24px;
  min-height: 420px;
  max-height: 85vh;
  overflow: auto;
  background: #e9edf2;

  /* --- 1:1 with the PDF freeform renderer. Sizes are pt × 4/3 (96dpi);
     colours, weights and spacing are copied from export-runner/src/render
     (theme.ts + pdf/renderFlow.tsx). Keep these in lock-step. --- */
  .ProseMirror {
    outline: none;
    /* Same TTFs the renderer embeds (see FreeformFonts) → matching line wraps. */
    font-family: 'PoppinsExport', 'Poppins', sans-serif;
    font-size: 14.67px; /* 11pt body */
    line-height: 1.6;
    color: #555862; /* davysGrey — PDF body text inherits this */
    /* Flex column so adjacent block margins do NOT collapse — react-pdf (Yoga)
       sums them, so this keeps the editor's block spacing 1:1 with the export. */
    display: flex;
    flex-direction: column;
    align-items: stretch;
  }
  .ProseMirror:focus {
    outline: none;
  }
  .ProseMirror strong,
  .ProseMirror b {
    font-weight: 600; /* renderers map bold → Poppins 600, never 700 */
  }
  .ProseMirror em {
    font-style: italic;
  }
  .ProseMirror u {
    text-decoration: underline;
  }
  .ProseMirror s {
    text-decoration: line-through;
  }
  .ProseMirror h1 {
    font-size: 32px; /* 24pt */
    font-weight: 600;
    line-height: 1.3; /* locked to the renderer's explicit heading lineHeight */
    color: #022033; /* prussianBlue */
    margin: 0 0 21.33px; /* mb 16pt */
  }
  .ProseMirror h2 {
    font-size: 21.33px; /* 16pt */
    font-weight: 600;
    line-height: 1.3;
    color: #022033;
    margin: 26.67px 0 10.67px; /* mt 20pt / mb 8pt */
  }
  .ProseMirror h3 {
    font-size: 17.33px; /* 13pt */
    font-weight: 500;
    line-height: 1.3;
    color: #022033;
    margin: 21.33px 0 8px; /* mt 16pt / mb 6pt */
  }
  .ProseMirror p {
    margin: 0 0 10.67px; /* mb 8pt */
  }
  .ProseMirror ul,
  .ProseMirror ol {
    margin: 0 0 10.67px;
    padding-left: 24px; /* 18pt marker column */
  }
  .ProseMirror ul {
    list-style: disc;
  }
  .ProseMirror ol {
    list-style: decimal;
  }
  .ProseMirror li {
    margin-bottom: 2.67px;
  }
  .ProseMirror li::marker {
    color: #022033; /* prussianBlue markers */
  }
  .ProseMirror li > p {
    margin: 0;
  }
  .ProseMirror blockquote {
    border-left: 4px solid #6a7590; /* cflowerBlue, 3pt bar */
    padding-left: 13.33px; /* 10pt */
    margin: 0 0 10.67px;
    color: #555862;
    font-style: normal; /* upright — matches the PDF blockquote */
  }
  .ProseMirror pre {
    background: #f6f6f9; /* cultured */
    border-radius: 4px;
    padding: 10.67px; /* 8pt */
    margin: 0 0 10.67px;
    font-family: 'Courier New', monospace;
    font-size: 13.33px; /* 10pt */
    line-height: 1.4; /* matches renderer codeText */
    color: #022033; /* prussianBlue */
    white-space: pre-wrap;
  }
  .ProseMirror code {
    /* inline code: Courier only, no background (PDF draws none) */
    font-family: 'Courier New', monospace;
  }
  .ProseMirror pre code {
    background: none;
    padding: 0;
    font-size: inherit;
    color: inherit;
  }
  .ProseMirror a {
    color: #2563eb;
    text-decoration: underline;
    cursor: pointer;
  }
  /* A top-level hr is a page/slide break in the renderer — flag it as one. */
  .ProseMirror hr {
    border: none;
    border-top: 2px dashed #6a7590;
    margin: 16px 0;
  }
  /* tables — full hairline cflowerBlue grid (row + column separators), rounded
     outer corners, cultured header fill, 9pt prussianBlue text. */
  .ProseMirror table {
    border-collapse: separate;
    border-spacing: 0;
    table-layout: fixed;
    width: 100%;
    margin: 0 0 13.33px;
    border: 1px solid #6a7590;
    border-radius: 4px;
    overflow: hidden;
  }
  .ProseMirror td,
  .ProseMirror th {
    padding: 9.33px 10.67px; /* 7pt / 8pt — matches renderer cell padding */
    height: 40px; /* renderer row minHeight 30pt = 40px (min, grows with content) */
    vertical-align: middle;
    box-sizing: border-box;
    position: relative;
    min-width: 1em;
    border-right: 1px solid #6a7590;
    border-bottom: 1px solid #6a7590;
    font-size: 12px; /* 9pt */
    line-height: 1.4; /* matches renderer tableCellText */
    color: #022033; /* prussianBlue */
  }
  .ProseMirror td:last-child,
  .ProseMirror th:last-child {
    border-right: none;
  }
  .ProseMirror tr:last-child td,
  .ProseMirror tr:last-child th {
    border-bottom: none;
  }
  .ProseMirror th {
    background: #f6f6f9; /* cultured header fill */
    font-weight: 600;
    text-align: left;
  }
  .ProseMirror td > p,
  .ProseMirror th > p {
    margin: 0;
    font-size: 12px;
    color: #022033;
  }
  .ProseMirror .selectedCell:after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(37, 99, 235, 0.12);
    pointer-events: none;
  }
  .ProseMirror .column-resize-handle {
    position: absolute;
    right: -2px;
    top: 0;
    bottom: 0;
    width: 4px;
    background: #2563eb;
    pointer-events: none;
  }
  .ProseMirror p.is-editor-empty:first-child::before {
    content: attr(data-placeholder);
    float: left;
    color: #9ca3af;
    pointer-events: none;
    height: 0;
  }

  /* --- multi-column layout. Invisible in the EXPORT; in the EDITOR each column
     shows a dashed boundary (dashed = "won't print") and a grabbable divider.
     NB: ReactNodeViewRenderer wraps each column in a div.react-renderer — THAT is
     the flex item (its flex-basis is set in ColumnView via JS). The gutter +
     last-child rules therefore live on the .react-renderer host, not .rc-column. --- */
  .ProseMirror .rc-columns {
    display: flex;
    flex-direction: row;
    align-items: flex-start; /* columns size to their own content (no stretch) */
    margin: 0 0 10.67px;
  }
  .ProseMirror .rc-columns > .react-renderer {
    box-sizing: border-box;
    min-width: 2.5ch; /* let a column shrink to ~2 characters */
    padding-right: 16px; /* gutter between columns (holds the resize divider) */
  }
  .ProseMirror .rc-columns > .react-renderer:last-child {
    padding-right: 0;
  }
  .ProseMirror .rc-column {
    position: relative;
  }
  /* The visible (editor-only) column box. No padding (content must sit exactly
     where it renders), square dashed border, and break long unbreakable words so
     a narrow column wraps like the export. */
  .ProseMirror .rc-col-content {
    box-sizing: border-box;
    min-height: 30px;
    padding: 5px 7px;
    border: 1px dashed #c7cddb;
    overflow-wrap: anywhere;
    word-break: break-word;
    transition: border-color 120ms ease;
  }
  .ProseMirror .rc-column:hover .rc-col-content {
    border-color: #6a7590;
  }
  /* Reset the first/last block's outer margins so the box hugs its content evenly
     (TipTap nests the real blocks inside a contentDOM wrapper, so match both the
     direct child and the grandchild — otherwise the last block's margin-bottom
     leaves a bigger gap at the bottom than the top/sides). */
  .ProseMirror .rc-col-content > :first-child,
  .ProseMirror .rc-col-content > * > :first-child {
    margin-top: 0;
  }
  .ProseMirror .rc-col-content > :last-child,
  .ProseMirror .rc-col-content > * > :last-child {
    margin-bottom: 0;
  }
  .ProseMirror .rc-col-controls {
    position: absolute;
    top: -12px;
    right: 4px;
    display: none;
    gap: 2px;
    z-index: 5;
    padding: 2px;
    background: #ffffff;
    border: 1px solid #e9ecff;
    border-radius: 6px;
    box-shadow: 0 1px 4px rgba(2, 32, 51, 0.12);
  }
  .ProseMirror .rc-column:hover > .rc-col-controls {
    display: flex;
  }
  .ProseMirror .rc-col-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border: none;
    background: transparent;
    border-radius: 4px;
    font-size: 11px;
    color: #475569;
    cursor: pointer;
  }
  .ProseMirror .rc-col-btn:hover {
    background: #f6f6f9;
    color: #022033;
  }
  /* Resize divider — always visible (a faint line sitting in the gutter), turns
     brightBlue on hover. right:-16px places it inside the host's gutter. */
  .ProseMirror .rc-col-resize {
    position: absolute;
    top: 0;
    bottom: 0;
    right: -16px;
    width: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: col-resize;
    z-index: 6;
  }
  .ProseMirror .rc-columns > .react-renderer:last-child .rc-col-resize {
    display: none;
  }
  .ProseMirror .rc-col-resize::after {
    content: '';
    width: 2px;
    height: 100%;
    border-radius: 2px;
    background: #e3e7f2;
    transition:
      background 120ms ease,
      width 120ms ease;
  }
  .ProseMirror .rc-col-resize:hover::after,
  .ProseMirror .rc-columns:hover .rc-col-resize::after {
    width: 3px;
    background: #2bbdef; /* brightBlue */
  }
`;

// --- Page surface geometry — kept in lock-step with the export-runner page
// chrome. All values px @96dpi (pt × 4/3). A4 = 794×1123; the pptx slide is a
// true 13.33×7.5in widescreen (1280×720), not A4.
export type PageSurface = 'a4-portrait' | 'a4-landscape' | 'slide';

// Exact px @96dpi (pt × 4/3) so the editor's content width equals the renderer's
// and line wraps match 1:1. A4 = 595.28 × 841.89 pt; slide = 13.33 × 7.5 in.
const SHEET: Record<PageSurface, { w: number; h: number }> = {
  'a4-portrait': { w: 793.71, h: 1122.52 },
  'a4-landscape': { w: 1122.52, h: 793.71 },
  slide: { w: 1280, h: 720 },
};

const SIDE_INSET = 48; // PDF 36pt margin / slide 0.5in margin (×4/3 = 48px)

// Header band height (logo + rule) = PDF header 60pt = 80px, both surfaces.
const headerBand = (_surface: PageSurface): number => 80;
// Content insets, matching the renderer exactly. PDF chrome on: header 60+15pt =
// 75pt → 100px top; footer reserve 60pt → 80px bottom. Chrome off: balanced 24pt
// margins (32px) — the renderer drops the footer strip and top-cram for these
// pages. Slide values approximate (pptx clips).
const topInset = (surface: PageSurface, chrome: boolean): number =>
  chrome ? 100 : surface === 'slide' ? 29 : 32; // chrome: 75pt header = 100px (both)
// slide: pptx now reflows across slides like PDF — content stops at the footer
// (7.1in → 38px) when chrome is on, or the slide edge (7.5in → 0) when off. A4
// reserves the footer band (80px) / balanced bare margin (32px).
const bottomInset = (surface: PageSurface, chrome: boolean): number =>
  surface === 'slide' ? (chrome ? 38 : 0) : chrome ? 80 : 32;

// Pixel size of one printable page for a surface — used by PageEditor to detect
// overflow (sheet taller than one page) and mark the page edges.
export const sheetSize = (surface: PageSurface): { w: number; h: number } => SHEET[surface];

// Content-area geometry for overflow marking: `top` is where the writable area
// starts (below the header), `capacity` is how much content one printed page
// holds once the header + footer reserve is subtracted. Breaks fall at
// top + k·capacity, so footers/headers are accounted for — not just sheet height.
export const pageMetrics = (
  surface: PageSurface,
  chrome: boolean,
): { top: number; bottom: number; capacity: number } => {
  const top = topInset(surface, chrome);
  const bottom = bottomInset(surface, chrome);
  return { top, bottom, capacity: SHEET[surface].h - top - bottom };
};

// The page sheet — exact output proportions so the editor is 1:1 with the
// rendered file. `min-height` guarantees at least one page; it grows as content
// overflows. Header/footer chrome is absolutely positioned inside it.
export const Page = styled.div<{ $surface: PageSurface }>`
  position: relative;
  box-sizing: border-box;
  margin: 0 auto;
  background: #ffffff;
  box-shadow:
    0 1px 3px rgba(15, 23, 42, 0.18),
    0 8px 24px rgba(15, 23, 42, 0.12);
  overflow: hidden;
  width: ${({ $surface }) => SHEET[$surface].w}px;
  min-height: ${({ $surface }) => SHEET[$surface].h}px;
  transition:
    width 160ms ease,
    min-height 160ms ease;
`;

// Writable area. Padding reproduces the renderer's content margins; the inner
// `.ProseMirror` fills it so clicks anywhere inside land in the editor.
export const PageContent = styled.div<{ $surface: PageSurface; $chrome: boolean }>`
  box-sizing: border-box;
  padding: ${({ $surface, $chrome }) =>
    `${topInset($surface, $chrome)}px ${SIDE_INSET}px ${bottomInset($surface, $chrome)}px`};

  .ProseMirror {
    min-height: ${({ $surface, $chrome }) =>
      SHEET[$surface].h - topInset($surface, $chrome) - bottomInset($surface, $chrome)}px;
  }
`;

export const PageHeader = styled.div<{ $surface: PageSurface }>`
  position: absolute;
  top: 0;
  left: ${SIDE_INSET}px;
  right: ${SIDE_INSET}px;
  height: ${({ $surface }) => headerBand($surface)}px;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #e9ecff; /* stroke */
`;

// Workspace logo — sized like the PDF header logo (85×40pt → contain, left).
export const LogoImg = styled.img`
  max-width: 113px;
  max-height: 53px;
  object-fit: contain;
  object-position: left center;
`;

// Stand-in shown when the workspace has no logo configured.
export const LogoPlaceholder = styled.div`
  width: 113px; /* 85pt */
  height: 53px; /* 40pt */
  border: 1px dashed #c7cddb;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9aa3b8;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const PageFooter = styled.div<{ $surface: PageSurface }>`
  position: absolute;
  left: ${SIDE_INSET}px;
  right: ${SIDE_INSET}px;
  bottom: ${({ $surface }) => ($surface === 'slide' ? 30 : 19)}px; /* PDF 14pt */
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-top: 13px;
  border-top: 1px solid #e9ecff;
`;

export const PageNumber = styled.span`
  font-size: 13.33px; /* 10pt */
  color: #555862; /* davysGrey */
`;

// --- Multi-page management ---------------------------------------------------

// Wraps one page (bar + sheet). `width: fit-content` + `margin: 0 auto` centres
// the sheet when it fits and overflows to the right (no left clip) when wide.
export const PageWrap = styled.div`
  width: fit-content;
  margin: 0 auto;

  & + & {
    margin-top: 16px;
  }
`;

export const PageBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

export const PageLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
`;

export const RemovePageButton = styled.button`
  border: 1px solid #e9ecff;
  background: #ffffff;
  color: #6a7590;
  border-radius: 6px;
  padding: 3px 9px;
  font-family: inherit;
  font-size: 12px;
  cursor: pointer;
  transition:
    border-color 120ms ease,
    color 120ms ease;
  &:hover:not(:disabled) {
    border-color: #e32121; /* error */
    color: #e32121;
  }
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

// Zoom scaffolding. The content is absolutely positioned and scaled from its
// top-left; the viewport is sized to the SCALED bounds so the desk scrolls
// correctly at any zoom (and doesn't leave dead scroll space when zoomed out).
export const ZoomViewport = styled.div`
  position: relative;
  margin: 0 auto;
`;

export const ZoomContent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: fit-content;
  transform-origin: top left;
  /* Zoom snaps — no transform transition (a tween here lags the page-break
     re-measure and feels sluggish). */
  will-change: transform;
`;

// Bottom zoom control bar (sits under the scrollable desk, always visible).
export const ZoomBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  padding: 6px 10px;
  border-top: 1px solid #e9ecff;
  background: #f6f6f9;
`;

export const ZoomButton = styled.button`
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e9ecff;
  background: #ffffff;
  border-radius: 6px;
  font-size: 16px;
  line-height: 1;
  color: #022033;
  cursor: pointer;
  transition:
    border-color 120ms ease,
    background 120ms ease;
  &:hover:not(:disabled) {
    border-color: #6a7590;
    background: #f6f6f9;
  }
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

export const ZoomValue = styled.button`
  min-width: 54px;
  height: 28px;
  padding: 0 8px;
  border: 1px solid transparent;
  background: transparent;
  border-radius: 6px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  color: #022033;
  cursor: pointer;
  font-variant-numeric: tabular-nums;
  &:hover {
    background: #f6f6f9;
  }
`;

export const AddPageButton = styled.button`
  display: block;
  margin: 16px auto 0;
  border: 1px dashed #c7cddb;
  background: #ffffff;
  color: #6a7590; /* cflowerBlue — neutral utility action, not the pink accent */
  border-radius: 8px;
  padding: 10px 20px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition:
    border-color 120ms ease,
    background 120ms ease,
    color 120ms ease;
  &:hover:not(:disabled) {
    border-color: #6a7590;
    color: #022033; /* prussianBlue on hover */
    background: #f6f6f9;
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

// Dashed line marking where one printable page ends (top set inline per break).
export const PageEdge = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  border-top: 1px dashed #c2492f;
  pointer-events: none;
  z-index: 2;
`;

// Badge flagging that a page's content spills past one printable page.
export const OverflowBadge = styled.div`
  position: absolute;
  right: 10px;
  z-index: 3;
  background: #fff4ed;
  color: #b3431f;
  border: 1px solid #f3c4ad;
  border-radius: 6px;
  padding: 3px 8px;
  font-size: 11px;
  pointer-events: none;
`;

// --- Template debug modal (button → modal → per-template tabs) ---------------

export const OpenTemplatesButton = styled.button`
  border: 1px solid #2563eb;
  background: #eff6ff;
  color: #1d4ed8;
  border-radius: 6px;
  padding: 9px 12px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 120ms ease,
    border-color 120ms ease;
  &:hover {
    background: #dbeafe;
  }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 5vh 16px;
  overflow: auto;
`;

export const Modal = styled.div`
  width: 560px;
  max-width: 100%;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.3);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: 'Poppins', 'Inter', system-ui, sans-serif;
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  background: #1f2937;
  color: #ffffff;
`;

export const ModalTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.2px;
`;

export const ModalClose = styled.button`
  border: none;
  background: transparent;
  color: #cbd5e1;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
  &:hover {
    color: #ffffff;
  }
`;

export const TabBar = styled.div`
  display: flex;
  gap: 2px;
  padding: 8px 12px 0;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
`;

export const TabButton = styled.button<{ $active: boolean }>`
  border: none;
  background: ${({ $active }) => ($active ? '#ffffff' : 'transparent')};
  color: ${({ $active }) => ($active ? '#111827' : '#6b7280')};
  border: 1px solid ${({ $active }) => ($active ? '#e5e7eb' : 'transparent')};
  border-bottom: ${({ $active }) => ($active ? '1px solid #ffffff' : '1px solid transparent')};
  margin-bottom: -1px;
  border-radius: 6px 6px 0 0;
  padding: 8px 12px;
  font-family: inherit;
  font-size: 12px;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  cursor: pointer;
`;

export const ModalBody = styled.div`
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-height: 62vh;
  overflow: auto;
`;

export const ModalFooter = styled.div`
  display: flex;
  gap: 8px;
  padding: 14px 18px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
`;

// Native select styled to match the page's controls.
export const Select = styled.select`
  width: 100%;
  background: #ffffff;
  color: #111827;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 8px 10px;
  font-family: inherit;
  font-size: 13px;
  cursor: pointer;
  &:focus {
    outline: none;
    border-color: #2563eb;
  }
`;

export const NumberInput = styled.input`
  width: 100%;
  background: #ffffff;
  color: #111827;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 8px 10px;
  font-family: inherit;
  font-size: 13px;
  &:focus {
    outline: none;
    border-color: #2563eb;
  }
`;

// Scrollable id checklist (controls / scenarios).
export const Checklist = styled.div`
  max-height: 160px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 4px 0;
`;

export const CheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 10px;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
  &:hover {
    background: #f3f4f6;
  }
  input {
    accent-color: #2563eb;
    cursor: pointer;
  }
`;

export const Aside = styled.div`
  width: 300px;
  flex-shrink: 0;
  position: sticky;
  top: 12px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  padding: 16px;

  ${({ theme }) => theme.breakpoints.down('md')} {
    width: 100%;
    position: static;
  }
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const Label = styled.span`
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  font-size: 10px;
  font-weight: 600;
`;

export const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
  user-select: none;

  input {
    width: 15px;
    height: 15px;
    accent-color: #2563eb;
    cursor: pointer;
  }
  &:has(input:disabled) {
    opacity: 0.5;
    cursor: default;
  }
`;

export const FormatToggle = styled.div`
  display: flex;
  gap: 2px;
  background: #f3f4f6;
  padding: 3px;
  border-radius: 6px;
`;

export const FormatButton = styled.button<{ $selected: boolean }>`
  flex: 1;
  background: ${({ $selected }) => ($selected ? '#ffffff' : 'transparent')};
  color: ${({ $selected }) => ($selected ? '#111827' : '#6b7280')};
  border: none;
  padding: 7px 10px;
  border-radius: 4px;
  cursor: pointer;
  font-family: inherit;
  font-size: 12px;
  font-weight: ${({ $selected }) => ($selected ? 600 : 500)};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  box-shadow: ${({ $selected }) => ($selected ? '0 1px 2px rgba(15, 23, 42, 0.1)' : 'none')};
  transition: background 120ms ease;
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export const GenerateButton = styled.button<{ $variant: 'node' | 'docker' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: ${({ $variant }) => ($variant === 'docker' ? '#2563eb' : '#059669')};
  color: #ffffff;
  border: none;
  padding: 11px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.2px;
  transition:
    background 120ms ease,
    transform 80ms ease;
  &:hover:not(:disabled) {
    background: ${({ $variant }) => ($variant === 'docker' ? '#1d4ed8' : '#047857')};
  }
  &:active:not(:disabled) {
    transform: translateY(1px);
  }
  &:disabled {
    background: #9ca3af;
    cursor: default;
  }
`;

export const StatusBar = styled.div<{ $status: 'idle' | 'sent' | 'error' }>`
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.4;
  background: ${({ $status }) => ($status === 'sent' ? '#ecfdf5' : $status === 'error' ? '#fef2f2' : '#f9fafb')};
  color: ${({ $status }) => ($status === 'sent' ? '#065f46' : $status === 'error' ? '#991b1b' : '#4b5563')};
  border: 1px solid ${({ $status }) => ($status === 'sent' ? '#a7f3d0' : $status === 'error' ? '#fecaca' : '#e5e7eb')};
`;

export const Hint = styled.div`
  font-size: 11px;
  color: #6b7280;
  line-height: 1.5;
`;
